<template>
  <tr
    v-if="bluredOrNotHidden"
    :class="{ active: isActive, hovered: showActions }"
    class="draggableBlock"
    data-block="Divider"
    @mouseover="mouseOver"
    @mouseleave="mouseOut"
    @click.stop="() => click()"
  >
    <td
      :class="{
        'block-borders': showDragBorder,
        'dragged-opacity': isCurrentElementDragged,
      }"
      :style="{
        padding: padding,
        backgroundColor: bgColor,
        backgroundImage: gradient,
      }"
      style="word-break: break-word; font-size: 0px"
    >
      <blockActions
        v-if="showActions"
        :column-padding-x="columnPaddingX"
        :column-width="columnWidth"
        :disable-up-movement="disableUpMovement"
        :disable-down-movement="disableDownMovement"
        :block-height="blockHeight"
        :block-width="blockWidth"
        :is-active="isActive"
        :is-last-column="isLastColumn"
        :section-index="sectionIndex"
        :column-index="columnIndex"
        :block-index="blockIndex"
        :show-paste="showPaste"
        :copied="copied"
        @duplicate="copyBlock"
        @remove="deleteBlock"
        @moveUp="moveUp"
        @moveDown="moveDown"
        @copy="copyToClipboard"
      ></blockActions>
      <p
        :id="id"
        style="font-size: 1px; margin: 0px auto; width: 100%"
        :style="{
          borderColor: block.attributes['border-color'],
          borderTop: borderTop,
          ...hiddenStyle,
        }"
      ></p>
    </td>
  </tr>
</template>

<script lang="ts" setup>
  import blockProperties from "@/composables/useBlockProperties";
  import blockActions from "./blockActions.vue";
  import { TopolBlock } from "@/schemas/templateSchema";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { computed, onMounted } from "vue";

  const mjmlStore = useMjmlStore();

  const props = defineProps<{
    block: TopolBlock;
    columnIndex: number;
    sectionIndex: number;
    blockIndex: number;
    isLocked: boolean;
  }>();

  const {
    showActions,
    showPaste,
    blockHeight,
    blockWidth,
    id,
    bluredOrNotHidden,
    hiddenStyle,
    isActive,
    disableUpMovement,
    disableDownMovement,
    columnPaddingX,
    columnWidth,
    isCurrentElementDragged,
    showDragBorder,
    isLastColumn,
    copied,
    mouseOver,
    mouseOut,
    click,
    copyBlock,
    deleteBlock,
    moveUp,
    moveDown,
    copyToClipboard,
  } = blockProperties(props);

  const padding = computed(() => {
    if (props.block.attributes["padding"]) {
      return props.block.attributes["padding"];
    }

    if (
      props.block.attributes["padding-top"] !== undefined &&
      props.block.attributes["padding-right"] !== undefined
    ) {
      return (
        validatePadding(props.block.attributes["padding-top"]) +
        " " +
        validatePadding(props.block.attributes["padding-right"])
      );
    }

    if (
      props.block.attributes["padding-top"] !== undefined &&
      props.block.attributes["padding-right"] === undefined
    ) {
      return validatePadding(props.block.attributes["padding-top"]) + " 25px";
    }

    if (
      props.block.attributes["padding-top"] === undefined &&
      props.block.attributes["padding-right"] !== undefined
    ) {
      return "10px " + validatePadding(props.block.attributes["padding-right"]);
    }

    return "10px 25px";
  });

  const style = computed(() => {
    if (props.block.attributes["border-style"]) {
      return props.block.attributes["border-style"];
    }
    return "solid";
  });

  const borderTop = computed(() => {
    let width = "1px";
    if (parseInt(props.block.attributes["border-width"]) > 1) {
      width = props.block.attributes["border-width"];
    }
    return (
      validatePadding(width) +
      " " +
      style.value +
      " " +
      props.block.attributes["border-color"]
    );
  });

  const bgColor = computed(() => {
    const bgColor = props.block.attributes["container-background-color"];
    if (bgColor !== null || bgColor !== undefined) {
      return bgColor;
    }
    return "";
  });

  const gradient = computed(() => {
    if (props.block.attributes["container-gradient"]) {
      return props.block.attributes["container-gradient"];
    }
    return "";
  });

  /**
   * Validates if padding is in format "15 px"
   * and fixes number if it is not.
   **/
  function validatePadding(val: number | string) {
    if (typeof val === "number") {
      return val + "px";
    }

    return val;
  }

  function updateAttr(key: string, value: string) {
    mjmlStore.updateSpecificBlockProp({
      prop: key,
      value: value,
      product: true, //means updating attribute
      sectionIndex: props.sectionIndex,
      columnIndex: props.columnIndex,
      blockIndex: props.blockIndex,
    });
  }

  onMounted(() => {
    if (props.block.attributes["padding"] === undefined) {
      updateAttr(
        "padding",
        props.block.attributes["padding-top"] +
          " " +
          props.block.attributes["padding-right"]
      );
    }
  });
</script>
